import { type hfb } from "@repo/api";
import { Pill, Text, Variables, crossIcon } from "@repo/skapa";
import { t } from "@repo/translation";
import { hasItems, isChecked, toggleRecordArrayValue } from "@repo/utils";
import isEmpty from "lodash/isEmpty";

import { pixels } from "../../../Variables";
import {
  useOrderedCustomerMeetingPoints,
  useOrderedDepartments,
} from "../../../hooks/useCMP";
import { FilterCheckbox } from "../../FilterCheckbox";
import { FilterPill } from "../../FilterPill";
import { FlexDiv } from "../../FlexDiv";

interface SearchTeamFiltersProps {
  hfbs?: hfb[];
  buCodes?: string[];
  setHfbs: (hfbs?: hfb[]) => void;
  setBuCodes: (buCodes?: string[]) => void;
  clearAll: () => void;
}

export const SearchTeamFilters = ({
  hfbs,
  buCodes,
  setHfbs,
  setBuCodes,
  clearAll,
}: SearchTeamFiltersProps) => {
  const departments = useOrderedDepartments();
  const { data: businessUnits } = useOrderedCustomerMeetingPoints();

  const filterLength = (buCodes ?? []).length + (hfbs ?? []).length;

  const dropDownHeight = `calc(100vh - ${
    document
      .getElementById("search-team-hfb-filter__pill")
      ?.getBoundingClientRect().top
  }px - 60px)`; // 102px is the modal footer height

  return (
    <FlexDiv flexDirection="column" gap={Variables.space75}>
      <FlexDiv gap={Variables.space75}>
        <FilterPill
          id="search-team-bu-filter__pill"
          label={t("label.business_unit")}
          selected={!isEmpty(buCodes)}
          maxHeight={dropDownHeight}
          dropDownItems={
            <FlexDiv flexDirection="column" gap={pixels.space10}>
              <FilterCheckbox
                id="search-team-bu-filter-select-all"
                value="all"
                label={
                  <Text textStyle="Heading.XS">
                    {hasItems(buCodes)
                      ? t("action.deselect_all")
                      : t("action.select_all")}
                  </Text>
                }
                checked={buCodes?.length === businessUnits.length}
                onCheckFilter={() => {
                  setBuCodes(
                    hasItems(buCodes)
                      ? undefined
                      : businessUnits.map((el) => el[0])
                  );
                }}
              />
              {businessUnits.map((el) => (
                <FilterCheckbox
                  key={el[0]}
                  id={el[0]}
                  value={el[0]}
                  label={el[1]}
                  checked={isChecked(buCodes, el[0])}
                  onCheckFilter={() =>
                    setBuCodes(toggleRecordArrayValue(buCodes, el[0]))
                  }
                />
              ))}
            </FlexDiv>
          }
        />
        <FilterPill
          id="search-team-hfb-filter__pill"
          label={t("label.department")}
          selected={!isEmpty(hfbs)}
          maxHeight={dropDownHeight}
          rightAlign
          dropDownItems={
            <FlexDiv flexDirection="column" gap={pixels.space10}>
              <FilterCheckbox
                id="search-team-hfb-filter-select-all"
                value="all"
                label={
                  <Text textStyle="Heading.XS">
                    {hasItems(hfbs)
                      ? t("action.deselect_all")
                      : t("action.select_all")}
                  </Text>
                }
                checked={hfbs?.length === departments.length}
                onCheckFilter={() => {
                  setHfbs(
                    hasItems(hfbs) ? undefined : departments.map((el) => el[0])
                  );
                }}
              />
              {departments.map((el) => (
                <FilterCheckbox
                  key={el[0]}
                  id={el[0]}
                  value={el[0]}
                  label={el[1]}
                  checked={isChecked(hfbs, el[0])}
                  onCheckFilter={() =>
                    setHfbs(toggleRecordArrayValue(hfbs, el[0]))
                  }
                />
              ))}
            </FlexDiv>
          }
        />
      </FlexDiv>
      {filterLength > 0 && (
        <FlexDiv gap={Variables.space75}>
          <Pill
            size="small"
            selected
            label={
              filterLength === 1
                ? t("prompt.one_filter_selected")
                : t("prompt.multiple_filters_selected", {
                    number: filterLength,
                  })
            }
            onClick={clearAll}
            leadingIcon={crossIcon}
          />
        </FlexDiv>
      )}
    </FlexDiv>
  );
};
